import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Building2, Home, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const AUD = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
const LAB_LABEL = {
  pending_sample: "Awaiting sample", awaiting_drop_off: "Awaiting drop-off",
  received: "Received", results_entered: "Results entered",
  submitted_for_review: "In review", in_review: "In review",
  published: "Published", failed: "Failed",
};
const REPORT_LABEL = {
  awaiting_results: "Awaiting results", draft: "Draft",
  ready: "Ready", published: "Published", sent: "Sent",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}
function fmtSpend(cents) {
  if (!cents || Number.isNaN(cents)) return "$0";
  return AUD.format(cents / 100);
}

export default function AdminPartnerCustomerDetail() {
  const { partnerId, customerKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(
          `/partner/customers/${encodeURIComponent(customerKey)}`,
          { params: { as_partner_id: partnerId } },
        );
        if (cancelled) return;
        setData(r.data);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Failed to load customer");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerId, customerKey]);

  if (loading) {
    return (
      <PortalShell title="Customer" subtitle="Loading…" wide>
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 text-sm">
          Loading…
        </div>
      </PortalShell>
    );
  }
  if (error || !data) {
    return (
      <PortalShell title="Customer" subtitle="Not found" wide>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
          {error || "Customer not found"}
        </div>
        <Link to="/admin/partners/customers" className="mt-4 inline-flex items-center gap-2 text-sm text-slate-700 hover:text-cyan-700">
          <ArrowLeft className="w-4 h-4" /> Back to Partner Customers
        </Link>
      </PortalShell>
    );
  }

  const c = data.customer || {};
  return (
    <PortalShell title={c.name || "Customer"} subtitle="Admin — partner scope" wide>
      <div className="space-y-6" data-testid="admin-partner-customer-detail-page">

        <Link to="/admin/partners/customers" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-cyan-700">
          <ArrowLeft className="w-4 h-4" /> Back to Partner Customers
        </Link>

        {/* Contact block */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Contact</div>
            <div className="text-slate-900 font-semibold">{c.name || "—"}</div>
            {c.email && (
              <a href={`mailto:${c.email}`} className="mt-1 flex items-center gap-1.5 text-sm text-slate-700 hover:text-cyan-700">
                <Mail className="w-4 h-4" /> {c.email}
              </a>
            )}
            {c.phone && (
              <a href={`tel:${c.phone}`} className="mt-1 flex items-center gap-1.5 text-sm text-slate-700 hover:text-cyan-700">
                <Phone className="w-4 h-4" /> {c.phone}
              </a>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Property</div>
            <div className="flex items-start gap-1.5 text-sm text-slate-700">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{c.address || "—"}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-xs px-2.5 py-1">
                <Building2 className="w-3.5 h-3.5" /> {data.properties_count} propert{data.properties_count === 1 ? "y" : "ies"}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-xs px-2.5 py-1">
                <Home className="w-3.5 h-3.5" /> {data.bookings_count} booking{data.bookings_count === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Lifetime</div>
            <div className="text-3xl font-bold text-slate-900 tabular-nums">
              {fmtSpend(data.lifetime_spend_cents)}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              First booked {fmtDate(data.first_booked_at)} · Last {fmtDate(data.last_booked_at)}
            </div>
          </div>
        </div>

        {/* Bookings timeline */}
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="px-5 pt-4 pb-3 border-b border-slate-200">
            <div className="text-slate-900 font-semibold">Booking history</div>
            <div className="text-xs text-slate-500">Most recent first — admin can jump into any booking</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left py-3 px-4">Reference</th>
                  <th className="text-left py-3 px-4">Date</th>
                  <th className="text-left py-3 px-4">Test</th>
                  <th className="text-right py-3 px-4">Amount</th>
                  <th className="text-left py-3 px-4">Lab</th>
                  <th className="text-left py-3 px-4">Report</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.bookings || []).map((b) => (
                  <tr key={b.reference} className="hover:bg-slate-50/60">
                    <td className="py-3 px-4 font-mono text-xs text-slate-700">{b.reference}</td>
                    <td className="py-3 px-4 text-slate-600">{fmtDate(b.created_at)}</td>
                    <td className="py-3 px-4 text-slate-700">{b.test_name || b.test_id || "—"}</td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      {typeof b.amount === "number" ? fmtSpend(b.amount) : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                        {LAB_LABEL[b.lab_status] || b.lab_status || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                        {REPORT_LABEL[b.report_status] || b.report_status || "—"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <Link
                        to={`/admin/bookings/${b.reference}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white text-xs font-semibold px-2.5 py-1.5 hover:bg-slate-800"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
